/**
 * Centralized RBAC utilities.
 *
 * Permissions format: resource.action (e.g., job.view, user.delete)
 */

/**
 * Normalize a permission string.
 */
const normalize = (perm) => String(perm ?? "").trim().toLowerCase();

/**
 * Checks if `permission` exists in `permissions`.
 * Supports basic wildcards:
 * - resource.*  -> any action under resource
 * - *.action     -> any resource for an action
 * - *            -> everything
 */
export const hasPermission = (permission, permissions = []) => {
    const p = normalize(permission);
    if (!p) return false;

    const set = new Set((permissions || []).map(normalize));
    if (set.has(p)) return true;

    const [resource, action] = p.split(".");
    if (!resource || !action) return false;

    // resource.*
    if (set.has(`${resource}.*`)) return true;
    // *.action
    if (set.has(`*.${action}`)) return true;
    // *
    if (set.has("*")) return true;

    return false;
};

/**
 * Checks if user has ANY of the permissions.
 */
export const hasAnyPermission = (permissionsToCheck = [], permissions = []) => {
    if (!Array.isArray(permissionsToCheck)) return false;
    return permissionsToCheck.some((p) => hasPermission(p, permissions));
};

/**
 * Helper to get permissions from auth user.
 *
 * No role-name fallback here on purpose: a user with no permissions must see
 * no UI, even if their role happens to be named "admin". Real admin status
 * comes through as actual permission codes (or the `*` wildcard), not from
 * guessing off the role string.
 */
export const getUserPermissions = (user) => {
    return Array.isArray(user?.permissions) ? user.permissions : [];
};

